import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useFiltersByName } from 'src/hooks/useFiltersByName';
import { IObject } from 'src/models/objectManagement';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import SearchIcon from '@mui/icons-material/Search';
import { useSelectItemDefaultSelected } from 'src/hooks/useSelectItemDefaultSelected';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useUnlimitedScroll } from 'src/hooks/useUnlimitedScroll';
import GetSocialMediaIconWithBgColor from 'src/components/GetSocialMediaIconWithBgColor';
import { ITabList } from '..';
import Empty from 'src/components/Empty';
import { useAppSelector } from 'src/app/hooks';
import ModalAddSocialProfileRetail from '../../ModalAddSocialProfileRetail';
import ModalAddKeywordAndHashtagRetail from '../../ModalAddKeywordAndHashtagRetail';
import ModalAddSocialProfile from 'src/components/ModalAddSocialProfile';
import ModalAddKeywordAndHashtag from 'src/components/ModalAddKeywordAndHashtag';
import ModalAddPostLink from 'src/components/ModalAddPostLink';

export type ITabValue = 'all' | 'account' | 'keyword' | 'hashtag' | 'post_link';

export default function SelectObjectSection({
  objectList,
  activeObjectIdList,
  selectedTab,
  setSelectedTab,
  tabList,
  onSelectObject,
  isGoogleReview
}: {
  objectList: Array<IObject>;
  activeObjectIdList: Array<string>;
  tabList: ITabList[];
  selectedTab: ITabValue;
  setSelectedTab: React.Dispatch<React.SetStateAction<ITabValue>>;
  onSelectObject: (objectIds: string[]) => void;
  isGoogleReview: boolean;
}) {
  const theme = useTheme();

  const { groupSelectedById } = useAppSelector(
    (state) => state.objectManagement
  );
  const customerType = useAppSelector(
    (state) => state.users.project.customerType
  );
  const isRetail = customerType === 'b2c';

  const filteredObjectListByTab = useMemo(() => {
    if (selectedTab === 'all') return objectList;
    if (selectedTab === 'account')
      return isGoogleReview
        ? objectList.filter(
            (item) =>
              item.streamType === 'account' &&
              item.socialMedia === 'googleReview'
          )
        : objectList.filter((item) => item.streamType === 'account');
    if (selectedTab === 'keyword')
      return objectList.filter(
        (item) => item.streamType === 'keyword' && !item.isHashtag
      );
    if (selectedTab === 'hashtag')
      return objectList.filter(
        (item) => item.streamType === 'keyword' && item.isHashtag
      );
    if (selectedTab === 'post_link')
      return objectList.filter((item) => item.streamType === 'post_link');
  }, [selectedTab, objectList, isGoogleReview]);

  const addName = useMemo((): Array<IObject & { name: string }> => {
    return filteredObjectListByTab.map((object) => ({
      ...object,
      name: object.objectName
    }));
  }, [filteredObjectListByTab]);

  const { filteredList: unsortedFilteredList, handleFilter } =
    useFiltersByName<IObject>(addName);

  const filteredList = useMemo(() => {
    if (!activeObjectIdList.length) return unsortedFilteredList;
    if (!unsortedFilteredList.length) return [];

    const activeIdsSet = new Set(activeObjectIdList);
    return unsortedFilteredList.sort((a, b) => {
      const aIsActive = activeIdsSet.has(a.id);
      const bIsActive = activeIdsSet.has(b.id);
      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;
      return 0;
    });
  }, [unsortedFilteredList, activeObjectIdList]);

  const {
    selectedItemByIds,
    handleClick,
    isSelected,
    handleSelectAll,
    handleClearAllSelection
  } = useSelectItemDefaultSelected<IObject>(filteredList, activeObjectIdList);

  const filteredSelectedObjectByTab = useMemo(() => {
    if (selectedTab === 'all')
      return objectList.filter((item) => selectedItemByIds.includes(item.id));
    if (selectedTab === 'account')
      return isGoogleReview
        ? objectList.filter(
            (item) =>
              item.streamType === 'account' &&
              selectedItemByIds.includes(item.id) &&
              item.socialMedia === 'googleReview'
          )
        : objectList.filter(
            (item) =>
              item.streamType === 'account' &&
              selectedItemByIds.includes(item.id)
          );
    if (selectedTab === 'keyword')
      return objectList.filter(
        (item) =>
          item.streamType === 'keyword' &&
          !item.isHashtag &&
          selectedItemByIds.includes(item.id)
      );
    if (selectedTab === 'hashtag')
      return objectList.filter(
        (item) =>
          item.streamType === 'keyword' &&
          item.isHashtag &&
          selectedItemByIds.includes(item.id)
      );
    if (selectedTab === 'post_link')
      return objectList.filter(
        (item) =>
          item.streamType === 'post_link' && selectedItemByIds.includes(item.id)
      );
  }, [selectedItemByIds, selectedTab, objectList, isGoogleReview]);

  const { isCheckedAll, isIndeterminate } = useMemo(() => {
    const selectedIdsSet = new Set(selectedItemByIds);
    const checkedItemsCount = filteredList.filter((el) =>
      selectedIdsSet.has(el.id)
    ).length;
    const isCheckedAll = filteredList.length === checkedItemsCount;
    const isIndeterminate =
      checkedItemsCount > 0 && checkedItemsCount < filteredList.length;

    return { isCheckedAll, isIndeterminate };
  }, [filteredList, selectedItemByIds]);

  const { fetchMoreData, infiniteScroll } =
    useUnlimitedScroll<IObject>(filteredList);

  const filteredInfiniteScroll = useMemo(() => {
    if (selectedTab === 'all') return infiniteScroll;
    if (selectedTab === 'account')
      return {
        ...infiniteScroll,
        list: infiniteScroll.list.filter(
          (item) => item.streamType === 'account'
        )
      };
    if (selectedTab === 'keyword')
      return {
        ...infiniteScroll,
        list: infiniteScroll.list.filter(
          (item) => item.streamType === 'keyword' && !item.isHashtag
        )
      };
    if (selectedTab === 'hashtag')
      return {
        ...infiniteScroll,
        list: infiniteScroll.list.filter(
          (item) => item.streamType === 'keyword' && item.isHashtag
        )
      };
    if (selectedTab === 'post_link')
      return {
        ...infiniteScroll,
        list: infiniteScroll.list.filter(
          (item) => item.streamType === 'post_link'
        )
      };
  }, [infiniteScroll, selectedTab]);

  useEffect(() => {
    onSelectObject(selectedItemByIds);
  }, [selectedItemByIds]);

  return (
    <>
      <Stack
        height="380px"
        direction="column"
        justifyContent="space-between"
        width="20%"
      >
        <Stack sx={{ borderRadius: '16px', border: 1, borderColor: '#E0E0E0' }}>
          {tabList.map((item, index) => {
            return (
              <>
                <Stack
                  key={item.value}
                  sx={{
                    py: 1,
                    px: 2,
                    borderStartStartRadius: index === 0 ? '16px' : 0,
                    borderStartEndRadius: index === 0 ? '16px' : 0,
                    borderEndStartRadius:
                      index === tabList.length - 1 ? '16px' : 0,
                    borderEndEndRadius:
                      index === tabList.length - 1 ? '16px' : 0,
                    cursor: 'pointer',
                    backgroundColor:
                      item.value === selectedTab
                        ? theme.colors.primary.light
                        : 'white'
                  }}
                  onClick={() => setSelectedTab(item.value)}
                >
                  <Typography fontSize="14px">{item.label}</Typography>
                </Stack>
                {index !== tabList.length - 1 && <Divider />}
              </>
            );
          })}
        </Stack>
        <Stack width="100%" spacing={1} alignItems="center">
          <Typography fontSize="14px">
            {filteredSelectedObjectByTab.length}/
            {filteredObjectListByTab.length}
          </Typography>
          <Typography fontSize="14px">Objects selected</Typography>
          <Box
            sx={{
              border: 1,
              borderColor: theme.colors.primary.main,
              color: theme.colors.primary.main,
              py: 1,
              px: 4,
              fontSize: '14px',
              fontWeight: 600,
              borderRadius: '16px',
              cursor: 'pointer',
              ':hover': {
                opacity: 0.7
              }
            }}
            onClick={handleClearAllSelection}
          >
            Clear All
          </Box>
        </Stack>
      </Stack>
      <Stack spacing={1} width="100%">
        <Stack
          sx={{
            py: 1,
            px: 2,
            borderRadius: '16px',
            border: 1,
            borderColor: '#E0E0E0'
          }}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography fontWeight={700}>All Objects</Typography>
          {groupSelectedById === '_default' && selectedTab === 'account' ? (
            isRetail ? (
              <ModalAddSocialProfileRetail buttonVariant="outlined" />
            ) : (
              <ModalAddSocialProfile title="Social Profile" />
            )
          ) : null}
          {groupSelectedById === '_default' &&
          (selectedTab === 'keyword' || selectedTab === 'hashtag') ? (
            isRetail ? (
              <ModalAddKeywordAndHashtagRetail
                streamType={selectedTab}
                buttonVariant="outlined"
              />
            ) : (
              <ModalAddKeywordAndHashtag
                title={
                  selectedTab.charAt(0).toUpperCase() + selectedTab.slice(1)
                }
                buttonLabel={
                  selectedTab.charAt(0).toUpperCase() + selectedTab.slice(1)
                }
                streamType={selectedTab}
              />
            )
          ) : null}
          {groupSelectedById === '_default' &&
          selectedTab === 'post_link' &&
          !isRetail ? (
            <ModalAddPostLink title="Post Link" buttonLabel="Post Link" />
          ) : null}
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              placeholder="Search Object"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ width: '75%' }}
              onChange={(e) => handleFilter(e.target.value)}
            />
            <Box sx={{ minWidth: theme.spacing(15) }}>
              <FormControlLabel
                control={
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon />}
                    checkedIcon={<CheckBoxIcon />}
                    indeterminateIcon={<IndeterminateCheckBoxIcon />}
                    indeterminate={isIndeterminate}
                  />
                }
                label="Select All"
                onChange={handleSelectAll}
                checked={isCheckedAll}
              />
            </Box>
          </Stack>
        </Stack>
        <Grid
          sx={{
            borderRadius: '16px',
            border: 1,
            borderColor: '#E0E0E0',
            overflow: 'auto',
            padding: 0,
            gap: 0
          }}
          container
          width="100%"
        >
          <Box width="100%">
            <InfiniteScroll
              dataLength={filteredInfiniteScroll.list.length}
              next={fetchMoreData}
              hasMore={filteredInfiniteScroll.hasMore}
              loader={<div />}
              height={250}
              endMessage={<div />}
            >
              {filteredInfiniteScroll.list.length > 0 ? (
                filteredInfiniteScroll.list.map((item, index) => {
                  const isItemSelected = isSelected(item.id);
                  return (
                    <>
                      <Grid
                        item
                        xs={12}
                        sx={{
                          p: 1,
                          borderStartStartRadius: index === 0 ? '16px' : 0,
                          borderStartEndRadius: index === 0 ? '16px' : 0,
                          borderEndStartRadius:
                            index === filteredList.length - 1 ? '16px' : 0,
                          borderEndEndRadius:
                            index === filteredList.length - 1 ? '16px' : 0,
                          cursor: 'pointer',
                          backgroundColor: isItemSelected
                            ? theme.colors.primary.light
                            : 'white'
                        }}
                        onClick={(event) => handleClick(event, item.id)}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          {isItemSelected ? (
                            <CheckBoxIcon />
                          ) : (
                            <CheckBoxOutlineBlankIcon />
                          )}
                          <GetSocialMediaIconWithBgColor
                            socialMedia={item.socialMedia}
                          />
                          <Typography fontSize="12px">
                            {item.streamType === 'account'
                              ? '@'
                              : item.streamType === 'keyword' && !item.isHashtag
                              ? '~'
                              : ''}
                            {item.objectName}
                          </Typography>
                        </Stack>
                      </Grid>
                      {index !== filteredList.length - 1 && (
                        <Divider color="black" />
                      )}
                    </>
                  );
                })
              ) : (
                <Empty />
              )}
            </InfiniteScroll>
          </Box>
        </Grid>
      </Stack>
    </>
  );
}
